/** Formatting helpers. Money from the API is integer paise; dates are ISO strings in UTC and shown in IST. */

const TZ = 'Asia/Kolkata';

/** 199900 -> "₹1,999"; 235882 -> "₹2,358.82". */
export function inr(paise) {
  const rupees = Number(paise || 0) / 100;
  const whole = Number.isInteger(rupees);
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** Rupee text typed by a person ("1,999", "₹500") to paise. Returns NaN when it is not a number. */
export function toPaise(text) {
  const cleaned = String(text ?? '').replace(/[₹,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
  return Math.round(Number(cleaned) * 100);
}

function parseDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(value, options = {}) {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ, ...options });
}

export function fmtTime(value) {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: TZ }).replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

export function fmtDateTime(value) {
  const d = parseDate(value);
  return d ? `${fmtDate(d)}, ${fmtTime(d)}` : '';
}

function istDayKey(d) {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

/** "Today, 7:00 PM", "Tomorrow, 7:00 PM", "Wed, 7:00 PM" or "12 Oct, 7:00 PM". */
export function fmtWhen(value, now = new Date()) {
  const d = parseDate(value);
  if (!d) return '';
  const days = Math.round((new Date(istDayKey(d)) - new Date(istDayKey(now))) / 86400000);
  const time = fmtTime(d);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Tomorrow, ${time}`;
  if (days === -1) return `Yesterday, ${time}`;
  if (days > 1 && days < 7) return `${d.toLocaleDateString('en-IN', { weekday: 'short', timeZone: TZ })}, ${time}`;
  return `${fmtDate(d, { year: undefined })}, ${time}`;
}

/** "5 min ago", "3 hr ago", "Yesterday", "12 Oct". */
export function ago(value, now = new Date()) {
  const d = parseDate(value);
  if (!d) return '';
  const seconds = Math.round((now - d) / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr ago`;
  if (seconds < 172800) return 'Yesterday';
  return fmtDate(d, { year: undefined });
}

/** Seconds -> "12:40" or "1:28:05". */
export function mmss(total) {
  const s = Math.max(0, Math.round(Number(total) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export function initials(name) {
  return String(name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** A stable 0-359 hue from a string, for avatar colours. */
export function hue(text) {
  return [...String(text ?? '')].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 360, 7);
}

export function firstName(name) {
  return String(name ?? '').trim().split(/\s+/)[0] || '';
}

export function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

export function debounce(fn, ms = 250) {
  let timer;
  const wrapped = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => clearTimeout(timer);
  return wrapped;
}

/** Weekday numbers (0 = Monday, as the API uses) to "Mon, Wed, Fri". */
export function weekdayNames(days) {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (days || []).map((d) => names[d]).filter(Boolean).join(', ');
}

/** <input type="datetime-local"> value (read as India time) -> ISO string with the right instant. */
export function fromIstInput(value) {
  if (!value) return '';
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  const d = new Date(`${withSeconds}+05:30`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** ISO instant -> value for <input type="datetime-local"> in India time. */
export function toIstInput(value) {
  const d = parseDate(value);
  if (!d) return '';
  const shifted = new Date(d.getTime() + 5.5 * 3600 * 1000);
  return shifted.toISOString().slice(0, 16);
}
